import * as React from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

export interface ConfirmOptions {
  title: string;
  body: React.ReactNode;
  // The action button's words, e.g. "Start test" (never just "OK").
  confirmLabel: string;
  // 'warning' for changes customers feel (going live, charging more).
  tone?: 'primary' | 'warning' | 'error';
}

// The page's own confirmation, in the dashboard's style, in place of the
// browser's window.confirm. `confirm(options)` resolves true on the action
// button, false on Cancel, Escape or a click outside; render `dialog` once.
export function useConfirm() {
  const [request, setRequest] = React.useState<{ options: ConfirmOptions; resolve: (answer: boolean) => void } | null>(null);
  const confirm = React.useCallback(
    (options: ConfirmOptions) => new Promise<boolean>(resolve => setRequest({ options, resolve })),
    [],
  );
  const close = (answer: boolean) => {
    request?.resolve(answer);
    setRequest(null);
  };
  const dialog = (
    <Dialog open={!!request} onClose={() => close(false)} maxWidth="xs" fullWidth aria-labelledby="post-purchase-confirm-title">
      {request && (
        <>
          <DialogTitle id="post-purchase-confirm-title" sx={{ fontWeight: 600 }}>{request.options.title}</DialogTitle>
          <DialogContent>
            {typeof request.options.body === 'string' ? (
              <Typography variant="body2" color="text.secondary">{request.options.body}</Typography>
            ) : (
              request.options.body
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => close(false)}>Cancel</Button>
            <Button variant="contained" color={request.options.tone ?? 'primary'} onClick={() => close(true)} autoFocus>
              {request.options.confirmLabel}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
  return { confirm, dialog };
}
